import RequestTemplate from 'components/templates/RequestTemplate'
import { WARNING } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import React, { useEffect } from 'react'
import InformationModal from 'components/elements/modal/InformationModal'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import SelectDamagedImageGroup from 'components/elements/form/SelectDamagedImageGroup'
import PhotoPointNote from 'components/elements/items/PhotoPointNote'
import CheckBoxGroup from 'components/elements/form/CheckBoxGroup'
import CallModal from 'components/elements/modal/CallModal'
import styled from 'styled-components'
import {
  AccidentImage,
  AccidentImageKeys,
  ImageInfo,
  QnStatesProps,
  LossKeyStatusTypes,
  InsideVehicleStatusTypes,
  DestinationTypes,
  VehicleHeightStatusTypes,
} from 'states/QnState'
import { createOptions } from 'utils/commonUtils'
import {
  StatesProps,
  useAddressMatch,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import KsSelectDestinationContent from 'components/elements/items/KsSelectDestinationContent'
import { success } from 'utils/status'
import { initialQnEditState } from '../../../../../states/QnState'
import { getDomain, isKsDomain, isSaficDomain } from 'utils/domain'
import KsSelectVehicleModificationContent from 'components/elements/items/KsSelectVehicleModificationContent'
import DOMAIN from 'constants/domains'

const lossKeyStatusOptions = createOptions(
  VEHICLE_STATE_FORM.lossKeyStatus.options
)
const insideVehicleStatusOptions = createOptions(
  VEHICLE_STATE_FORM.insideVehicleStatus.options
)

/** ポップアップのコンテンツ */
const CautionMessage: React.FC = React.memo(function cautionMessage() {
  return (
    <div style={{ textAlign: 'left', padding: '0px 30px' }}>
      <div>{'急ぎ手配を進めるためにオペレーターにお繋ぎします。'}</div>
    </div>
  )
})

/**
 * QN-03-g: カギのトラブル
 */
const LossKey: React.FC<StatesProps> = (props: QnStatesProps) => {
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()
  const { state, setState } = props
  const {
    lossKeyStatus,
    destinationType,
    insideVehicleStatus,
    modalFlag,
    vehicleHeightStatus,
  } = editState.vehicleStateInfo

  /** ポップアップの開閉フラグ */
  const [isOpenCaution, setIsOpenCaution] = React.useState<
    boolean | undefined
  >()

  // モーダル表示処理
  useEffect(() => {
    if (
      insideVehicleStatus.some((x) =>
        x.includes(InsideVehicleStatusTypes.confined)
      ) &&
      modalFlag === false
    ) {
      setIsOpenCaution(true)
      setVehicleStateInfo({
        modalFlag: true,
      })
    }
  }, [insideVehicleStatus, editState])

  //モーダル非表示処理
  useEffect(() => {
    if (
      !insideVehicleStatus.some((x) =>
        x.includes(InsideVehicleStatusTypes.confined)
      ) &&
      modalFlag === true
    ) {
      setVehicleStateInfo({
        modalFlag: false,
      })
    }
  }, [insideVehicleStatus])

  /** 配送先とトラブル場所のバリデーション */
  const addressMatchError = useAddressMatch(editState)

  // バリデーション
  const isDisabled = React.useMemo(() => {
    if (
      lossKeyStatus.includes(LossKeyStatusTypes.loss) &&
      destinationType.includes(DestinationTypes.specify)
    ) {
      if (!editState.destinationLocation.isSelected) {
        return true
      }
      return addressMatchError
    }
    return false
  }, [
    editState.vehicleStateInfo,
    editState.destinationLocation,
    addressMatchError,
  ])

  // 警告メッセージ
  const warning = React.useMemo(() => {
    if (!lossKeyStatus.includes(LossKeyStatusTypes.loss)) {
      return ''
    }
    switch (destinationType) {
      case DestinationTypes.specify:
        return isKsDomain() ? WARNING.WRECKER_KS : WARNING.WRECKER
      case DestinationTypes.introduction:
        if (
          // 共済系ドメイン時 かつ「入庫先紹介希望」選択時
          isKsDomain()
        ) {
          return ''
        }
        return WARNING.INTRODUCTION
    }
  }, [destinationType, lossKeyStatus])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo(() => {
    const transitionDestination = isKsDomain()
      ? PAGE_PATH.CONTRACT_INFORMATION
      : isSaficDomain()
      ? PAGE_PATH.SERVICE_INFORMATION
      : {
          [DOMAIN.SJ]: PAGE_PATH.CONTRACT_INFORMATION,
        }[getDomain()]
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : transitionDestination
  }, [state.confirmationChangeType, getDomain()])

  /** トラブルの状況押下時の処理 */
  const handleLossKeyStatusChanged = React.useCallback(
    (value: LossKeyStatusTypes) => {
      setVehicleStateInfo({
        lossKeyStatus: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** 車内の状況押下時の処理 */
  const handleInsideVehicleStatusChanged = React.useCallback(
    (value: InsideVehicleStatusTypes[]) => {
      setVehicleStateInfo({
        insideVehicleStatus: value,
      })
    },
    [setVehicleStateInfo]
  )

  const handleImageChanged = React.useCallback(
    (name: AccidentImageKeys, imageInfo: ImageInfo) => {
      const accidentImage: AccidentImage = {
        ...editState.vehicleStateInfo.accidentImage,
      }
      accidentImage[name] = imageInfo
      setVehicleStateInfo({
        accidentImage,
      })
    },
    [editState, setVehicleStateInfo]
  )

  const forwardAction = React.useCallback(() => {
    const QnStateDefaultInfoImage =
      initialQnEditState.vehicleStateInfo.accidentImage
    setEditingItem({
      ...editState,
      vehicleStateInfo: {
        ...editState.vehicleStateInfo,
        accidentImage: editState.vehicleStateInfo.isAddPhoto
          ? {
              ...editState.vehicleStateInfo.accidentImage,
              damagedPart: QnStateDefaultInfoImage.damagedPart,
            }
          : {
              ...editState.vehicleStateInfo.accidentImage,
              damagedPart: QnStateDefaultInfoImage.damagedPart,
              vehicleCloseUp: QnStateDefaultInfoImage.vehicleCloseUp,
              landmark: QnStateDefaultInfoImage.landmark,
              carVerification: QnStateDefaultInfoImage.carVerification,
              supplement: QnStateDefaultInfoImage.supplement,
            },
      },
    })
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: {
        ...editState.vehicleStateInfo,
        accidentImage: editState.vehicleStateInfo.isAddPhoto
          ? {
              ...editState.vehicleStateInfo.accidentImage,
              damagedPart: QnStateDefaultInfoImage.damagedPart,
            }
          : {
              ...editState.vehicleStateInfo.accidentImage,
              damagedPart: QnStateDefaultInfoImage.damagedPart,
              vehicleCloseUp: QnStateDefaultInfoImage.vehicleCloseUp,
              landmark: QnStateDefaultInfoImage.landmark,
              carVerification: QnStateDefaultInfoImage.carVerification,
              supplement: QnStateDefaultInfoImage.supplement,
            },
      },
      destinationLocation: editState.destinationLocation,
    })
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    setEditingItem({
      ...editState,
      vehicleStateInfo: state.vehicleStateInfo,
      troubleDatetime: state.troubleDatetime,
      vehicleState: state.vehicleState,
      destinationLocation: state.destinationLocation,
    })
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.troubleDatetime,
    state.vehicleState,
    state.destinationLocation,
  ])

  // EditingItemに "初期値" または "すでに選択済みのもの" をセットする
  useEffect(() => {
    setVehicleStateInfo({
      lossKeyStatus:
        lossKeyStatus === '' ? LossKeyStatusTypes.insideCar : lossKeyStatus,
      destinationType:
        destinationType === '' ? DestinationTypes.specify : destinationType,
      vehicleHeightStatus:
        vehicleHeightStatus === ''
          ? VehicleHeightStatusTypes.moreThan9cm
          : vehicleHeightStatus,
    })
  }, [])

  return (
    <>
      <RequestTemplate
        title={'カギのトラブル'}
        showContact
        progress={useProgressPoint()}
        description={
          '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
        }
        content={
          <React.Fragment>
            {lossKeyStatus.includes(LossKeyStatusTypes.insideCar) && (
              <CallModal isTransitionReAccess>
                <InformationModal
                  on={isOpenCaution || false}
                  content={<CautionMessage />}
                  greyBack
                  modalFill
                  textCenter
                  closeLabel={'OK'}
                  closeFunction={(): void => {
                    setIsOpenCaution(false)
                  }}
                />
              </CallModal>
            )}
            <SelectDamagedImageGroup
              accidentImage={editState.vehicleStateInfo.accidentImage}
              title={`${VEHICLE_STATE_FORM.accidentImage.description}`}
              isSinglePhoto={true}
              isRequired={!editState.vehicleStateInfo.noPhoto}
              onChanged={handleImageChanged}
              addPhoto={editState.vehicleStateInfo.isAddPhoto}
            />
            <PhotoPointNote />
            <RadioButtonGroup
              title={`${VEHICLE_STATE_FORM.lossKeyStatus.formName}を選択`}
              isRequired={true}
              options={lossKeyStatusOptions}
              selectedValue={lossKeyStatus}
              onValueChanged={handleLossKeyStatusChanged}
            />
            {lossKeyStatus.includes(LossKeyStatusTypes.insideCar) ? (
              <>
                <CheckBoxGroup
                  title={`${VEHICLE_STATE_FORM.insideVehicleStatus.formName}`}
                  options={insideVehicleStatusOptions}
                  selectedValues={insideVehicleStatus}
                  onValueChanged={handleInsideVehicleStatusChanged}
                />
                <Text>
                  鍵を開ける作業の特殊性から、現場にて身分証の確認をさせていただく場合がございますのでご了承ください。
                </Text>
              </>
            ) : (
              <>
                {isKsDomain() ? (
                  <KsSelectVehicleModificationContent />
                ) : (
                  <SelectVehicleModificationContent />
                )}
                {isKsDomain() ? (
                  <KsSelectDestinationContent isIntroduction={true} />
                ) : (
                  <SelectDestinationContent />
                )}
              </>
            )}
          </React.Fragment>
        }
        warning={warning}
        forwardTo={forwardTo}
        showBack
        forwardAction={forwardAction}
        backAction={backAction}
        backTo={PAGE_PATH.VEHICLE_STATE}
        isDisabled={isDisabled}
        notClearEditingItem
      />
    </>
  )
}

const Text = styled.div<{ fontSize?: string }>`
  display: flex;
  align-items: center;
  padding: 10px 16px 6px;
  ${(props): string => (props.fontSize ? `font-size:${props.fontSize}` : '')};
`

export default LossKey
